import React, { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  GeneralSelect,
  OptionType,
} from 'v2/form/CustomSelectors/GeneralSelect';
import classnames from 'classnames';
import styles from './fileSelection.module.scss';

import { parse } from 'papaparse';
import { useEntityOptions } from '../../models';
import FileInput from 'v2/form/FileInput/FileInput';
import LinksInAccordion, {
  Link,
} from 'v2/common/BulkImport/BulkImportModal/LinksInAccordion';
import { BulkImportType, useBulkImportTypeOptions } from './hooks';
import { isDRMEntityBulkImport } from '../../utils';

export interface FileSelectionFormFields {
  bulkImportType: BulkImportType;
  file: File;

  mapping?: Record<string, string | undefined>;
  store?: Record<string, OptionType | undefined>;
  headers?: string[];

  name: string;
  type: string;
  description: string;
}

export const helpLinks: Link[] = [
  {
    label: 'View import guide',
    url: 'https://help.nomad-data.com/how-do-i-import-my-existing-data-into-the-drm',
  },
  {
    label: 'Download sample spreadsheet',
    url: 'https://help.nomad-data.com/bulk-upload-file-format',
  },
];

interface DRMBulkFileSelectionProps {
  bulkImportType: BulkImportType;
  isBulkUploadFileLoading: boolean;

  goToStep: (id: string | number) => void;
  onBulkImportTypeChange: (bulkImportType?: BulkImportType) => void;
}

const allRowsWithSameColumns = (data: string[][]) => {
  const headers = data[0];
  return data.every((row) => row.length === headers.length);
};

const noEmptyHeaders = (data: string[][]) => {
  const headers = data[0];
  return headers.every((header) => header);
};

const noRepeatedHeaders = (data: string[][]) => {
  const headers = data[0];
  const deduplicateHeaders = new Set(headers);

  return headers.length === deduplicateHeaders.size;
};

const DRMBulkFileSelection = ({
  isBulkUploadFileLoading,
  bulkImportType,
  goToStep,
  onBulkImportTypeChange,
}: DRMBulkFileSelectionProps) => {
  const entityOptions = useEntityOptions();
  const { control, resetField, setValue } =
    useFormContext<FileSelectionFormFields>();
  const { file } = useWatch<FileSelectionFormFields>();
  const [parsedFile, setParsedFile] = useState<File | undefined>();

  const [headers, setHeaders] = useState<string[]>([]);

  const options = useBulkImportTypeOptions();
  const defaultOption = options.find(
    (option) => option.value === bulkImportType
  );

  useEffect(() => {
    if (file) {
      parse<string[]>(file, {
        skipEmptyLines: true,
        complete: (result) => {
          if (result.data.length < 2) {
            resetField('file');
            goToStep('MissingColumnHeaders');
            return;
          }

          if (!allRowsWithSameColumns(result.data)) {
            resetField('file');
            goToStep('RowsWithoutSameColumns');
            return;
          }

          if (!noEmptyHeaders(result.data)) {
            resetField('file');
            goToStep('EmptyHeaders');
            return;
          }

          if (!noRepeatedHeaders(result.data)) {
            resetField('file');
            goToStep('RepeatedHeaders');
            return;
          }

          const fileHeaders = result.data[0];

          if (bulkImportType) {
            const requiredHeaders = entityOptions[bulkImportType][0].options;

            if (fileHeaders.length < requiredHeaders.length) {
              resetField('file');
              goToStep('MissingRequiredHeaders');
              return;
            }
          }

          setParsedFile(file);
          setHeaders(fileHeaders);
          setValue('headers', fileHeaders);
        },
      });
    } else {
      setParsedFile(undefined);
    }
  }, [file]);

  useEffect(() => {
    if (bulkImportType && isDRMEntityBulkImport(bulkImportType)) {
      for (const fileHeader of headers) {
        setValue(`store.${fileHeader}`, undefined);
      }
    }
  }, [bulkImportType]);

  return (
    <>
      <div>
        <strong className={classnames(styles.label)}>
          What are you importing?
        </strong>
        <Controller
          name="bulkImportType"
          control={control}
          rules={{
            required: true,
          }}
          defaultValue={defaultOption?.value}
          render={({ field: { value, onChange } }) => {
            const selectedOption = options.find(
              (option) => option.value === value
            );

            return (
              <GeneralSelect
                options={options}
                handleChange={(option) => {
                  onChange(option?.value);
                  onBulkImportTypeChange(option?.value);
                }}
                selectedOption={selectedOption}
                disabled={isBulkUploadFileLoading}
                fullWidth
              />
            );
          }}
        />
      </div>

      <>
        <FileInput
          control={control}
          file={parsedFile}
          resetField={resetField}
          accept="text/csv"
        />
        <LinksInAccordion
          links={helpLinks}
          header="Need help getting started?"
        />
      </>
    </>
  );
};

export default DRMBulkFileSelection;
